import React, { useEffect, useRef, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { motion } from 'framer-motion';

// Import Jupiter plugin CSS
import '@jup-ag/plugin/css';

// SOL mint address
const SOL_MINT = 'So11111111111111111111111111111111111111112';

// Jupiter referral account for collecting swap fees (0.5% fee, 0.4% after Jupiter's cut)
const REFERRAL_ACCOUNT = 'HDra3BVXgjV8QV4ZnDFGuT6n5jZrvz8rCv3zZpixLEHP';
const REFERRAL_FEE_BPS = 50; // 50 basis points = 0.5%

export const SwapPage: React.FC = () => {
  const wallet = useWallet();
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const jupiterInitialized = useRef(false);

  const handleConnectWallet = useCallback(() => {
    setWalletModalVisible(true);
  }, [setWalletModalVisible]);

  // Initialize Jupiter plugin
  useEffect(() => {
    const initJupiter = async () => {
      if (jupiterInitialized.current) return;
      jupiterInitialized.current = true;

      // Dynamically import to ensure client-side only
      const { init } = await import('@jup-ag/plugin');

      init({
        displayMode: 'integrated',
        integratedTargetId: 'jupiter-plugin',
        enableWalletPassthrough: true,
        passthroughWalletContextState: wallet,
        onRequestConnectWallet: handleConnectWallet,
        defaultExplorer: 'Solscan',
        containerClassName: 'jupiter-arcade-theme',
        formProps: {
          initialOutputMint: SOL_MINT,
          referralAccount: REFERRAL_ACCOUNT,
          referralFee: REFERRAL_FEE_BPS,
        },
      });
    };

    initJupiter();
  }, [wallet, handleConnectWallet]);

  // Sync wallet state changes with Jupiter
  useEffect(() => {
    const syncWallet = async () => {
      if (!jupiterInitialized.current) return;
      const { syncProps } = await import('@jup-ag/plugin');
      syncProps({ passthroughWalletContextState: wallet });
    };
    syncWallet();
  }, [wallet]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-2xl space-y-6 py-8"
    >
      {/* Header */}
      <header>
        <h1 className="neon-text font-display text-3xl font-bold">Token Swap</h1>
        <p className="mt-2 text-sm text-white/50">
          Swap tokens to get SOL or USDC for wagers. Powered by Jupiter.
        </p>
      </header>

      {/* Jupiter Widget Container */}
      <section className="relative overflow-hidden rounded-2xl border border-arcade-cyan/30 bg-gradient-to-br from-arcade-cyan/5 via-transparent to-arcade-purple/5">
        {!wallet.connected && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <svg
                className="h-8 w-8 text-white/30"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <p className="mb-4 font-arcade text-lg text-white/50">
              Connect your wallet to swap tokens
            </p>
            <button
              onClick={handleConnectWallet}
              className="btn-press rounded-xl border border-arcade-cyan/50 bg-arcade-cyan/10 px-6 py-3 font-arcade text-sm text-arcade-cyan transition hover:bg-arcade-cyan/20"
            >
              Connect Wallet
            </button>
          </div>
        )}
        <div id="jupiter-plugin" className="min-h-[400px] sm:min-h-[500px] w-full" />
      </section>

      {/* Info Footer */}
      <footer className="text-center">
        <p className="text-xs text-white/30">
          Swaps are executed via Jupiter aggregator for the best rates across Solana DEXs
        </p>
      </footer>
    </motion.div>
  );
};
